import React, { useEffect, useState } from 'react';
import type { User } from '@supabase/supabase-js';
import { examService } from '@/services/examService';
import type { ExamEligibility, ExamMCQQuestion, UserAnswerIndex } from '@/types/exam';
import { Clock, AlertCircle, CheckCircle2, XCircle, HelpCircle } from 'lucide-react';
import ExamResultsModal from './ExamResultsModal';
import { useTouchFeedback } from '@/hooks/useTouchFeedback';

interface ExamTakingModalProps {
  exam: ExamEligibility;
  user: User | null;
  onClose: () => void;
  onCompleted: (examAttemptId: string) => void;
}

type ExamPhase = 'loading' | 'instruction' | 'taking' | 'review' | 'result' | 'error' | 'not-eligible';

const ExamTakingModal: React.FC<ExamTakingModalProps> = ({
  exam,
  user,
  onClose,
  onCompleted,
}) => {
  const { triggerHaptic } = useTouchFeedback({ enableHaptics: true });
  const [phase, setPhase] = useState<ExamPhase>('loading');
  const [questions, setQuestions] = useState<ExamMCQQuestion[]>([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [userAnswers, setUserAnswers] = useState<UserAnswerIndex[]>([]);
  const [timeStarted, setTimeStarted] = useState<number>(Date.now());
  const [timeSpent, setTimeSpent] = useState(0);
  const [result, setResult] = useState<{
    passed: boolean;
    score_percentage: number;
    correct_answers: number;
    total_questions: number;
    message: string;
    examAttemptId?: string;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showEndExamConfirm, setShowEndExamConfirm] = useState(false);
  const [reviewTab, setReviewTab] = useState<'all' | 'unanswered'>('all');

  useEffect(() => {
    loadQuestions();
  }, []);

  useEffect(() => {
    if (phase === 'taking') {
      const timer = setInterval(() => {
        setTimeSpent(Math.floor((Date.now() - timeStarted) / 1000));
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [phase, timeStarted]);

  const loadQuestions = async () => {
    if (!user?.id) return;

    try {
      setPhase('loading');
      const qs = await examService.getExamQuestions(
        exam.roadmap_id,
        exam.roadmap_difficulty
      );

      if (qs.length === 0) {
        setError('No questions available for this exam. Please try again later.');
        setPhase('error');
        return;
      }

      setQuestions(qs);
      setUserAnswers(new Array(qs.length).fill(null));
      setPhase('instruction');
    } catch (err) {
      console.error('Error loading questions:', err);
      setError('Failed to load exam questions.');
      setPhase('error');
    }
  };

  const handleStartExam = async () => {
    if (!user?.email) return;

    // Check eligibility when starting exam
    try {
      const eligibility = await examService.checkExamEligibility(
        user.id,
        exam.roadmap_id,
        user.email
      );

      if (!eligibility.is_eligible) {
        setPhase('not-eligible');
        setError(
          `You need to complete at least 75% of this roadmap to take the exam. ` +
          `Current progress: ${eligibility.completion_percentage.toFixed(0)}%`
        );
        return;
      }

      setTimeStarted(Date.now());
      setPhase('taking');
    } catch (err) {
      console.error('Error checking eligibility:', err);
      setError('Failed to verify exam eligibility.');
      setPhase('error');
    }
  };

  const handleSelectAnswer = (optionIndex: number) => {
    const newAnswers = [...userAnswers];
    newAnswers[currentQuestionIndex] = optionIndex;
    setUserAnswers(newAnswers);
  };

  const handleNextQuestion = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  const handlePreviousQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const handleSubmitExam = async () => {
    if (!user?.id) return;

    try {
      setIsSubmitting(true);
      const { examResult, examAttemptId } = await examService.submitExam(
        user.id,
        exam.roadmap_id,
        exam.roadmap_difficulty,
        questions,
        userAnswers,
        timeSpent
      );

      setResult({
        ...examResult,
        examAttemptId,
      });
      setPhase('result');
    } catch (err) {
      console.error('Error submitting exam:', err);
      setError('Failed to submit exam. Please try again.');
      setPhase('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReviewAnswers = () => {
    setPhase('review');
  };

  const handleEndExamNow = async () => {
    if (!user?.id) return;

    try {
      setIsSubmitting(true);
      const { examResult, examAttemptId } = await examService.submitExam(
        user.id,
        exam.roadmap_id,
        exam.roadmap_difficulty,
        questions,
        userAnswers,
        timeSpent
      );

      setResult({
        ...examResult,
        examAttemptId,
      });
      setPhase('result');
      setShowEndExamConfirm(false);
    } catch (err) {
      console.error('Error ending exam:', err);
      setError('Failed to submit exam. Please try again.');
      setPhase('error');
      setShowEndExamConfirm(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const currentQuestion = questions[currentQuestionIndex];
  const completedQuestions = userAnswers.filter((a) => a !== null).length;
  const formatTime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs
        .toString()
        .padStart(2, '0')}`;
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  if (phase === 'error') {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8 max-w-md w-full">
          <div className="flex items-center gap-3 mb-4 text-red-600">
            <AlertCircle className="w-6 h-6" />
            <h2 className="text-2xl font-bold">Error</h2>
          </div>
          <p className="text-gray-700 mb-6">{error}</p>
          <button
            onClick={onClose}
            className="w-full bg-red-600 text-white py-2 px-4 rounded-lg font-semibold hover:bg-red-700"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  if (phase === 'not-eligible') {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8 max-w-md w-full">
          <div className="flex items-center gap-3 mb-4 text-yellow-600">
            <AlertCircle className="w-6 h-6" />
            <h2 className="text-2xl font-bold">Not Eligible Yet</h2>
          </div>
          <p className="text-gray-700 mb-6">{error}</p>
          <p className="text-sm text-gray-600 mb-6">
            Complete more of the roadmap to unlock this exam and validate your skills.
          </p>
          <button
            onClick={onClose}
            className="w-full bg-yellow-600 text-white py-2 px-4 rounded-lg font-semibold hover:bg-yellow-700"
          >
            Return to Exams
          </button>
        </div>
      </div>
    );
  }

  if (phase === 'loading') {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8">
          <div className="flex justify-center mb-4">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
          </div>
          <p className="text-center text-gray-600">Loading exam...</p>
        </div>
      </div>
    );
  }

  if (phase === 'instruction') {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8 max-w-2xl w-full mx-4">
          <h2 className="text-3xl font-bold mb-6 text-gray-900">Exam Instructions</h2>

          <div className="space-y-4 mb-8">
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-blue-900 font-semibold mb-2">📝 Exam Details</p>
              <ul className="text-blue-800 space-y-1 text-sm">
                <li>• Roadmap: <span className="font-semibold">{exam.roadmap_name}</span></li>
                <li>• Level: <span className="font-semibold">{exam.roadmap_difficulty}</span></li>
                <li>• Total Questions: <span className="font-semibold">{questions.length}</span></li>
                <li>• Passing Score: <span className="font-semibold">75%</span></li>
              </ul>
            </div>

            <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
              <p className="text-amber-900 font-semibold mb-2">⚠️ Important Rules</p>
              <ul className="text-amber-800 space-y-1 text-sm">
                <li>• All questions are multiple choice (MCQ)</li>
                <li>• Each correct answer = 1 mark</li>
                <li>• You can review and change answers before submitting</li>
                <li>• Once submitted, you cannot redo the exam</li>
                <li>• You need 75% or more to pass</li>
              </ul>
            </div>

            <div className="p-4 bg-green-50 rounded-lg border border-green-200">
              <p className="text-green-900 font-semibold mb-2">🎯 If You Pass</p>
              <p className="text-green-800 text-sm">
                You'll receive a digital certificate with your score and a unique credential ID
                that you can verify and share.
              </p>
            </div>
          </div>

          <div className="flex gap-4">
            <button
              onClick={onClose}
              className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-lg font-semibold hover:bg-gray-400"
            >
              Cancel
            </button>
            <button
              onClick={handleStartExam}
              className="flex-1 bg-gradient-to-r from-red-500 to-orange-500 text-white py-2 px-4 rounded-lg font-semibold hover:shadow-lg"
            >
              Start Exam
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (phase === 'result' && result) {
    return (
      <ExamResultsModal
        exam={{
          roadmap_id: exam.roadmap_id,
          roadmap_name: exam.roadmap_name,
          roadmap_difficulty: exam.roadmap_difficulty,
        }}
        result={result}
        timeSpent={timeSpent}
        userId={user?.id || ''}
        questions={questions}
        userAnswers={userAnswers}
        onRetry={() => {
          setCurrentQuestionIndex(0);
          setUserAnswers(new Array(questions.length).fill(null));
          setTimeStarted(Date.now());
          setPhase('taking');
        }}
        onGetCertificate={(examAttemptId) => {
          // Close the exam modal and trigger certificate view
          onClose();
          onCompleted(examAttemptId);
        }}
        onClose={onClose}
      />
    );
  }

  if (phase === 'review') {
    const allQuestions = questions;
    const unansweredQuestions = questions
      .map((q, idx) => ({ question: q, index: idx }))
      .filter(({ index }) => userAnswers[index] === null);
    
    const questionsToShow = reviewTab === 'unanswered' ? unansweredQuestions : allQuestions.map((q, idx) => ({ question: q, index: idx }));

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          <div className="sticky top-0 bg-gradient-to-r from-red-500 to-orange-500 text-white p-6 flex justify-between items-center">
            <h2 className="text-2xl font-bold">Review Your Answers</h2>
            <button
              onClick={() => {
                setReviewTab('all');
                setPhase('taking');
              }}
              className="text-white hover:bg-white/20 px-4 py-2 rounded-lg"
            >
              ✕
            </button>
          </div>

          {/* Tabs */}
          <div className="sticky top-0 bg-gray-100 border-b border-gray-200 px-6 flex gap-1">
            <button
              onClick={() => setReviewTab('all')}
              className={`px-6 py-3 font-semibold transition-all ${
                reviewTab === 'all'
                  ? 'bg-white text-red-600 border-b-2 border-red-600'
                  : 'text-gray-700 hover:text-gray-900'
              }`}
            >
              All Questions ({questions.length})
            </button>
            <button
              onClick={() => setReviewTab('unanswered')}
              className={`px-6 py-3 font-semibold transition-all flex items-center gap-2 ${
                reviewTab === 'unanswered'
                  ? 'bg-white text-amber-600 border-b-2 border-amber-600'
                  : 'text-gray-700 hover:text-gray-900'
              }`}
            >
              <HelpCircle className="w-4 h-4" />
              Unanswered ({unansweredQuestions.length})
            </button>
          </div>

          <div className="p-6 space-y-6">
            {questionsToShow.length === 0 ? (
              <div className="text-center py-8">
                <CheckCircle2 className="w-12 h-12 text-green-600 mx-auto mb-3" />
                <p className="text-lg font-semibold text-gray-900">Great! All questions answered</p>
                <p className="text-gray-600 text-sm mt-1">You have answered all questions in the exam.</p>
              </div>
            ) : (
              (questionsToShow as typeof questionsToShow).map(({ question, index }) => (
                <div key={question.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-start justify-between mb-3">
                    <p className="font-semibold text-gray-900">
                      Question {index + 1}: {question.question}
                    </p>
                    {userAnswers[index] === null ? (
                      <HelpCircle className="w-5 h-5 text-amber-600 flex-shrink-0" />
                    ) : userAnswers[index] === question.correctAnswer ? (
                      <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" />
                    ) : (
                      <XCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                    )}
                  </div>

                  <div className="space-y-2">
                    {question.options.map((option, optionIndex) => {
                      const isSelected = userAnswers[index] === optionIndex;
                      const isCorrect = optionIndex === question.correctAnswer;

                      return (
                        <div
                          key={optionIndex}
                          className={`p-3 rounded-lg border-2 ${
                            isCorrect
                              ? 'bg-green-50 border-green-500'
                              : isSelected && !isCorrect
                                ? 'bg-red-50 border-red-500'
                                : userAnswers[index] === null
                                  ? 'bg-gray-50 border-gray-200'
                                  : 'bg-gray-50 border-gray-200'
                          }`}
                        >
                          <p className="text-sm text-gray-900">
                            {String.fromCharCode(65 + optionIndex)}. {option}
                            {isCorrect && ' ✓'}
                            {isSelected && !isCorrect && ' ✗'}
                          </p>
                        </div>
                      );
                    })}
                  </div>

                  {question.explanation && (
                    <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <p className="text-xs font-semibold text-blue-900 mb-1">Explanation:</p>
                      <p className="text-sm text-blue-800">{question.explanation}</p>
                    </div>
                  )}

                  {userAnswers[index] === null && (
                    <button
                      onClick={() => {
                        setCurrentQuestionIndex(index);
                        setReviewTab('all');
                        setPhase('taking');
                      }}
                      className="mt-3 w-full bg-amber-50 text-amber-700 hover:bg-amber-100 py-2 px-3 rounded-lg font-semibold text-sm transition-colors"
                    >
                      Answer This Question
                    </button>
                  )}
                </div>
              ))
            )}
          </div>

          <div className="sticky bottom-0 bg-gray-50 border-t p-6">
            <button
              onClick={() => {
                setReviewTab('all');
                setPhase('taking');
              }}
              className="w-full bg-gradient-to-r from-red-500 to-orange-500 text-white py-3 px-4 rounded-lg font-semibold hover:shadow-lg"
            >
              Continue to Exam
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Taking exam phase
  if (!currentQuestion) {
    return null;
  }

  // End exam confirmation dialog
  if (showEndExamConfirm) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4">
          <div className="flex items-center gap-3 mb-4 text-amber-600">
            <AlertCircle className="w-6 h-6" />
            <h2 className="text-2xl font-bold">End Exam Now?</h2>
          </div>
          
          <div className="space-y-4 mb-6">
            <p className="text-gray-700">
              Are you sure you want to end the exam now? Your current progress will be submitted.
            </p>
            
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <p className="text-sm text-amber-900">
                <span className="font-semibold">{completedQuestions}</span> of <span className="font-semibold">{questions.length}</span> questions answered
              </p>
              <p className="text-sm text-amber-800 mt-1">
                You will be scored only on the answers you've provided.
              </p>
            </div>
          </div>

          <div className="flex gap-4">
            <button
              onClick={() => setShowEndExamConfirm(false)}
              className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-lg font-semibold hover:bg-gray-400"
            >
              Continue Exam
            </button>
            <button
              onClick={handleEndExamNow}
              disabled={isSubmitting}
              className="flex-1 bg-gradient-to-r from-amber-500 to-orange-500 text-white py-2 px-4 rounded-lg font-semibold hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Submitting...' : 'End & Submit'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto flex flex-col">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-red-500 to-orange-500 text-white p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl font-bold">{exam.roadmap_name} - Exam</h2>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 bg-white/20 px-4 py-2 rounded-lg">
                <Clock className="w-5 h-5" />
                <span className="font-semibold">{formatTime(timeSpent)}</span>
              </div>
              <button
                onClick={() => setShowEndExamConfirm(true)}
                className="px-3 py-2 bg-red-600 hover:bg-red-700 rounded-lg text-sm font-semibold transition-colors"
                title="End exam and submit current answers"
              >
                End Now
              </button>
            </div>
          </div>
          <div className="w-full bg-white/30 rounded-full h-2">
            <div
              className="bg-white h-2 rounded-full transition-all"
              style={{
                width: `${((currentQuestionIndex + 1) / questions.length) * 100}%`,
              }}
            ></div>
          </div>
          <p className="text-sm mt-2">
            Question {currentQuestionIndex + 1} of {questions.length}
          </p>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Question */}
          <div className="mb-8">
            <h3 className="text-xl font-bold text-gray-900 mb-6">{currentQuestion.question}</h3>

            {/* Options */}
            <div className="space-y-3">
              {currentQuestion.options.map((option, index) => (
                <label
                  key={index}
                  className={`block p-4 rounded-lg border-2 cursor-pointer transition-all ${
                    userAnswers[currentQuestionIndex] === index
                      ? 'bg-red-50 border-red-500'
                      : 'bg-gray-50 border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <input
                    type="radio"
                    name="answer"
                    checked={userAnswers[currentQuestionIndex] === index}
                    onChange={() => handleSelectAnswer(index)}
                    className="mr-3"
                  />
                  <span className="font-medium text-gray-900">
                    {String.fromCharCode(65 + index)}. {option}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* Answer summary */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800">
              <span className="font-semibold">{completedQuestions}</span> of{' '}
              <span className="font-semibold">{questions.length}</span> questions answered
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-50 border-t p-6">
          <div className="flex gap-3 mb-4">
            <button
              onClick={handlePreviousQuestion}
              disabled={currentQuestionIndex === 0}
              className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-lg font-semibold hover:bg-gray-400 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              ← Previous
            </button>
            <button
              onClick={handleReviewAnswers}
              className="flex-1 bg-blue-500 text-white py-2 px-4 rounded-lg font-semibold hover:bg-blue-600"
            >
              Review
            </button>
            {currentQuestionIndex === questions.length - 1 ? (
              <button
                onClick={handleSubmitExam}
                disabled={isSubmitting || completedQuestions < questions.length}
                className="flex-1 bg-gradient-to-r from-green-500 to-green-600 text-white py-2 px-4 rounded-lg font-semibold hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Submitting...' : 'Submit Exam'}
              </button>
            ) : (
              <button
                onClick={handleNextQuestion}
                className="flex-1 bg-gradient-to-r from-red-500 to-orange-500 text-white py-2 px-4 rounded-lg font-semibold hover:shadow-lg"
              >
                Next →
              </button>
            )}
          </div>

          {completedQuestions < questions.length && (
            <p className="text-sm text-amber-700 bg-amber-50 p-3 rounded-lg">
              ⚠️ You have {questions.length - completedQuestions} unanswered question(s). Answer
              all questions before submitting.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExamTakingModal;
